import React, { useState, useEffect, useCallback } from 'react';
import DipSection from './DipSection';
import axios from 'axios';

const API = 'https://aaktradex.in';

const C = {
  green: '#3B6D11', lgreen: '#EAF3DE', bgreen: '#C0DD97',
  amber: '#854F0B', lamber: '#FAEEDA', bamber: '#FAC775',
  red: '#A32D2D', lred: '#FCEBEB', bred: '#F7C1C1',
  blue: '#185FA5', lblue: '#E6F1FB', bblue: '#B5D4F4',
  purple: '#6B21A8', lpurple: '#F3E8FF', bpurple: '#C4B5FD',
  gray: '#5F5E5A', lgray: '#F1EFE8',
};

const REGIME_COLORS = {
  STRONG_BULL: { bg: C.lgreen, fg: C.green, border: C.bgreen },
  MILD_BULL:   { bg: '#E8F5E9', fg: '#2E7D32', border: '#A5D6A7' },
  NEUTRAL:     { bg: C.lgray,  fg: C.gray,   border: '#D6D3C7' },
  MILD_BEAR:   { bg: C.lamber, fg: C.amber,  border: C.bamber },
  BEAR:        { bg: C.lred,   fg: C.red,    border: C.bred },
};

const DECISION_COLORS = {
  GO:    { bg: C.lgreen, fg: C.green, border: C.bgreen },
  WATCH: { bg: C.lblue,  fg: C.blue,  border: C.bblue },
  NO_GO: { bg: C.lgray,  fg: C.gray,  border: '#D6D3C7' },
  VETO:  { bg: C.lred,   fg: C.red,   border: C.bred },
};

function Pill({ label, colors, size = 11 }) {
  return (
    <span style={{
      fontSize: size, fontWeight: 600, padding: '3px 10px',
      borderRadius: 20, background: colors.bg, color: colors.fg,
      border: `1px solid ${colors.border}`, whiteSpace: 'nowrap',
    }}>{label}</span>
  );
}

function Card({ label, value, hint, color = C.blue }) {
  return (
    <div style={{
      padding: 14, background: '#FFFFFF', borderRadius: 10,
      border: '1px solid #E5E5E0', minWidth: 130,
    }}>
      <div style={{ fontSize: 10, color: '#8A8A8A', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color }}>{value}</div>
      {hint && <div style={{ fontSize: 10, color: '#8A8A8A', marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

function fmtNum(v, decimals = 2) {
  if (v === null || v === undefined) return '-';
  return Number(v).toFixed(decimals);
}
function fmtPct(v, decimals = 2) {
  if (v === null || v === undefined) return '-';
  return `${Number(v).toFixed(decimals)}%`;
}
function fmtValue(v) {
  if (v === null || v === undefined) return '-';
  return `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function StarChip({ text, isRed }) {
  const bg = isRed ? C.lred : C.lgreen;
  const fg = isRed ? C.red : C.green;
  return (
    <span style={{
      display: 'inline-block', fontSize: 10, padding: '2px 6px',
      margin: '2px 2px 0 0', background: bg, color: fg, borderRadius: 4,
      fontFamily: 'monospace',
    }}>{text}</span>
  );
}

function StockDetailModal({ ticker, forDate, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    axios.get(`${API}/intelligence/stocks/${ticker}${forDate ? `?for_date=${forDate}` : ''}`, { timeout: 15000 })
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(e => {
        if (!cancelled) {
          console.error('Intelligence detail fetch failed:', e);
          setError(e.response?.data?.detail || e.message || 'Fetch failed');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ticker, forDate]);

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }} onClick={onClose}>
      <div style={{
        background: '#FFFFFF', borderRadius: 12, padding: 24,
        maxWidth: 900, width: '100%', maxHeight: '85vh', overflowY: 'auto',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{ticker}</div>
            {data?.detail && (
              <div style={{ fontSize: 12, color: '#8A8A8A', marginTop: 2 }}>
                {data.detail.sector} · {data.detail.industry}
              </div>
            )}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#4A4A4A' }}>×</button>
        </div>

        {loading && <div style={{ padding: 20, textAlign: 'center', color: '#8A8A8A' }}>Loading...</div>}
        {error && <div style={{ padding: 20, textAlign: 'center', color: '#A32D2D' }}>Error: {error}</div>}

        {data?.detail && (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <Pill label={data.detail.decision} colors={DECISION_COLORS[data.detail.decision] || DECISION_COLORS.NO_GO} size={12} />
              <Pill label={`net ${data.detail.net_conviction}`} colors={{ bg: C.lblue, fg: C.blue, border: C.bblue }} size={12} />
              <Pill label={`green ${data.detail.green_score}`} colors={{ bg: C.lgreen, fg: C.green, border: C.bgreen }} size={12} />
              <Pill label={`red ${data.detail.red_score}`} colors={{ bg: C.lred, fg: C.red, border: C.bred }} size={12} />
              {data.detail.dvm_class && <Pill label={data.detail.dvm_class} colors={{ bg: C.lpurple, fg: C.purple, border: C.bpurple }} size={12} />}
              {data.detail.veto_reason && <Pill label={`VETO: ${data.detail.veto_reason}`} colors={DECISION_COLORS.VETO} size={12} />}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 16 }}>
              <div style={{ padding: 12, background: "#F8F7F3", border: "1px solid #E5E5E0", borderRadius: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: C.green }}>Green Stars ({data.detail.green_score})</div>
                {data.detail.green_reasons?.length ? data.detail.green_reasons.map((r,i) => <StarChip key={i} text={r} isRed={false} />) : <div style={{ fontSize: 11, color: '#8A8A8A' }}>None fired</div>}
              </div>
              <div style={{ padding: 12, background: "#F8F7F3", border: "1px solid #E5E5E0", borderRadius: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: C.red }}>Red Stars ({data.detail.red_score})</div>
                {data.detail.red_reasons?.length ? data.detail.red_reasons.map((r,i) => <StarChip key={i} text={r} isRed={true} />) : <div style={{ fontSize: 11, color: '#8A8A8A' }}>None fired</div>}
              </div>
            </div>

            {data.detail.decision === 'GO' && (
              <div style={{ padding: 12, marginBottom: 16, background: "#F8F7F3", border: "1px solid #E5E5E0", borderRadius: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Ladder + Sizing</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, fontSize: 11 }}>
                  <div><span style={{ color: '#8A8A8A' }}>Tier</span><br/><b>{data.detail.ladder_tier}</b></div>
                  <div><span style={{ color: '#8A8A8A' }}>Half-book at</span><br/><b>{data.detail.hb_pct ? `+${data.detail.hb_pct}%` : 'no HB'}</b></div>
                  <div><span style={{ color: '#8A8A8A' }}>Trail gap</span><br/><b>{fmtPct(data.detail.trail_gap_pct, 1)}</b></div>
                  <div><span style={{ color: '#8A8A8A' }}>SL × ATR</span><br/><b>{fmtNum(data.detail.sl_atr_mult, 2)}×</b></div>
                  <div><span style={{ color: '#8A8A8A' }}>Time backstop</span><br/><b>{data.detail.time_days}d</b></div>
                  <div><span style={{ color: '#8A8A8A' }}>Suggested qty</span><br/><b>{data.detail.suggested_qty}</b></div>
                  <div><span style={{ color: '#8A8A8A' }}>Suggested value</span><br/><b>{fmtValue(data.detail.suggested_value)}</b></div>
                  <div><span style={{ color: '#8A8A8A' }}>Final size mult</span><br/><b>{fmtNum(data.detail.final_size_mult, 2)}×</b></div>
                </div>
                <div style={{ fontSize: 10, color: '#8A8A8A', marginTop: 8 }}>
                  regime {fmtNum(data.detail.base_size_mult,2)}× · conviction {fmtNum(data.detail.conviction_mult,2)}× · ATR {fmtNum(data.detail.atr_norm,2)}× · earnings {fmtNum(data.detail.earnings_mult,2)}×
                </div>
              </div>
            )}

            {data.detail.days_to_earnings !== null && data.detail.days_to_earnings !== undefined && data.detail.days_to_earnings <= 10 && (
              <div style={{ padding: 12, marginBottom: 16, background: "#F8F7F3", border: "1px solid #E5E5E0", borderRadius: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Earnings Context</div>
                <div style={{ fontSize: 11 }}>
                  Next earnings in <b>{data.detail.days_to_earnings} day{data.detail.days_to_earnings === 1 ? '' : 's'}</b> ({data.detail.next_earnings_date})
                  {data.detail.last_qtr_surprise_pct !== null && data.detail.last_qtr_surprise_pct !== undefined && (
                    <> · Last quarter EPS surprise: <b style={{ color: data.detail.last_qtr_surprise_pct < 0 ? C.red : C.green }}>{fmtPct(data.detail.last_qtr_surprise_pct * 100, 1)}</b></>
                  )}
                  <br/>Action: <b>{data.detail.earnings_action}</b>
                  {data.detail.earnings_exit_at && <> · Forced exit at T-1: <b>{new Date(data.detail.earnings_exit_at).toLocaleString('en-IN')}</b></>}
                </div>
              </div>
            )}

            <div style={{ padding: 12, marginBottom: 16, background: "#F8F7F3", border: "1px solid #E5E5E0", borderRadius: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Market Data</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, fontSize: 11 }}>
                <div><span style={{ color: '#8A8A8A' }}>Close</span><br/><b>{fmtValue(data.detail.close_price)}</b></div>
                <div><span style={{ color: '#8A8A8A' }}>52W position</span><br/><b>{fmtPct(data.detail.pos_52w, 1)}</b></div>
                <div><span style={{ color: '#8A8A8A' }}>ATR (14)</span><br/><b>{fmtPct(data.detail.atr_pct, 2)}</b></div>
                <div><span style={{ color: '#8A8A8A' }}>vs EMA200</span><br/><b>{fmtPct(data.detail.pct_above_ema200, 2)}</b></div>
                <div><span style={{ color: '#8A8A8A' }}>FII QoQ</span><br/><b>{fmtPct(data.detail.fii_qoq, 2)}</b></div>
                <div><span style={{ color: '#8A8A8A' }}>FII 4Q</span><br/><b>{fmtPct(data.detail.fii_4q, 2)}</b></div>
                <div><span style={{ color: '#8A8A8A' }}>Near 52W high</span><br/><b>{data.detail.near_52w_high ? 'YES' : 'no'}</b></div>
              </div>
            </div>

            {data.history?.length > 1 && (
              <div style={{ padding: 12, background: "#F8F7F3", border: "1px solid #E5E5E0", borderRadius: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>History (last 30 days)</div>
                <table style={{ width: '100%', fontSize: 10.5, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ color: '#8A8A8A' }}>
                      <th style={{ textAlign: 'left', padding: '4px 6px' }}>Date</th>
                      <th style={{ textAlign: 'left', padding: '4px 6px' }}>Decision</th>
                      <th style={{ textAlign: 'right', padding: '4px 6px' }}>Net</th>
                      <th style={{ textAlign: 'right', padding: '4px 6px' }}>Green</th>
                      <th style={{ textAlign: 'right', padding: '4px 6px' }}>Red</th>
                      <th style={{ textAlign: 'left', padding: '4px 6px' }}>Veto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.history.map(h => (
                      <tr key={h.for_date} style={{ borderTop: '1px solid #E5E5E0' }}>
                        <td style={{ padding: '4px 6px' }}>{h.for_date}</td>
                        <td style={{ padding: '4px 6px' }}><Pill label={h.decision} colors={DECISION_COLORS[h.decision] || DECISION_COLORS.NO_GO} size={9} /></td>
                        <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600 }}>{h.net_conviction}</td>
                        <td style={{ padding: '4px 6px', textAlign: 'right', color: C.green }}>{h.green_score}</td>
                        <td style={{ padding: '4px 6px', textAlign: 'right', color: C.red }}>{h.red_score}</td>
                        <td style={{ padding: '4px 6px', color: '#8A8A8A', fontSize: 9 }}>{h.veto_reason || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// Why did THIS stock earn its stars — built from its own values.
// Evidence: 433,757 stock-days ATR-normalised, baseline 1.003 / 57.3% hit.
// r20>=20 alone 1.161/71.1%; +pos52>=85 1.223/71.7%; +vol>=1.5x 1.385/76.0%.
const alignWhy = (s) => {
  const legs = [
    ['20d return', s.ret_20d, 20, '>=20%'],
    ['52w position', s.pos_52w, 85, '>=85'],
    ['volume', s.vol_ratio20, 1.5, '>=1.5x'],
  ];
  const lines = legs.map((L) => {
    const val = L[1], ok = val != null && Number(val) >= L[2];
    const shown = val == null ? 'n/a' : Number(val).toFixed(2);
    return (ok ? '\u2713 ' : '\u2717 ') + L[0] + ' ' + shown + ' (needs ' + L[3] + ')';
  });
  const n = s.alignment || 0;
  const head = n === 3 ? 'TRIPLE ALIGNED - trend, position and volume all confirm'
    : n === 2 ? 'Two of three confirm'
    : n === 1 ? 'One of three confirms' : 'None of the three confirm';
  const stat = n === 3 ? '\n\n76% hit rate on +2% within 3 days vs 57% baseline.'
    : n === 2 ? '\n\nOn our trades 2-of-3 has run 71% WR (n=7, thin).' : '';
  return head + '\n' + lines.join('\n') + stat;
};

export default function IntelligencePanel() {
  const [summary, setSummary] = useState(null);
  const [stocks, setStocks] = useState([]);
  const [vetoes, setVetoes] = useState([]);
  const [gates, setGates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [decisionFilter, setDecisionFilter] = useState('GO');
  const [sectorFilter, setSectorFilter] = useState('');
  const [vetoFilter, setVetoFilter] = useState('');
  const [selectedTicker, setSelectedTicker] = useState(null);
  const [message, setMessage] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [runRes, stocksRes, vetoRes, gateRes] = await Promise.all([
        axios.get(`${API}/intelligence/runs/latest`),
        axios.get(`${API}/intelligence/stocks?${decisionFilter !== 'ALL' ? `decision=${decisionFilter}` : ''}${sectorFilter ? `&sector=${encodeURIComponent(sectorFilter)}` : ''}&limit=500`).then(r => { let stocks = r.data.stocks || []; if (vetoFilter) stocks = stocks.filter(x => x.veto_reason === vetoFilter); return { data: { stocks } }; }),
        axios.get(`${API}/intelligence/vetoes`),
        axios.get(`${API}/intelligence/gatestatus?limit=25`),
      ]);
      setSummary(runRes.data);
      setStocks(stocksRes.data.stocks || []);
      setVetoes(vetoRes.data.breakdown || []);
      setGates(gateRes.data.rows || []);
    } catch (e) {
      setMessage({ type: 'error', text: `Failed to load: ${e.response?.data?.detail || e.message}` });
    } finally {
      setLoading(false);
    }
  }, [decisionFilter, sectorFilter, vetoFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // 2026-08-14: poll every 120s, matching the scanner cycle. Gate status is
  // only useful live — it answers "why is rank 6 not being bought right now".
  useEffect(() => {
    const id = setInterval(() => { fetchData(); }, 120000);
    return () => clearInterval(id);
  }, [fetchData]);

  const triggerRerun = async () => {
    setRefreshing(true);
    setMessage(null);
    try {
      await axios.post(`${API}/intelligence/runs`);
      setMessage({ type: 'success', text: 'Rerun triggered. Refreshing in 20 seconds...' });
      setTimeout(() => { fetchData(); setRefreshing(false); }, 20000);
    } catch (e) {
      setMessage({ type: 'error', text: `Rerun failed: ${e.response?.data?.detail || e.message}` });
      setRefreshing(false);
    }
  };

  const sectors = Array.from(new Set(stocks.map(s => s.sector).filter(Boolean))).sort();

  if (!summary && loading) return <div style={{ padding: 24, textAlign: 'center', color: '#8A8A8A' }}>Loading intelligence layer...</div>;
  if (!summary) return <div style={{ padding: 24 }}>No intelligence runs yet. <button onClick={triggerRerun} style={{ marginLeft: 8, padding: '6px 16px', background: C.lblue, color: C.blue, border: `1px solid ${C.bblue}`, borderRadius: 6, cursor: 'pointer' }}>Trigger First Run</button></div>;

  const regime = summary.regime;
  const regimeColors = REGIME_COLORS[regime] || REGIME_COLORS.NEUTRAL;

  return (
    <div style={{ padding: 16, maxWidth: 1400 }}>
      {/* Top strip */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Intelligence · {summary.for_date}</h2>
        <Pill label={`${regime} · Nifty ${fmtPct(summary.nifty_chg_pct, 2)}`} colors={regimeColors} size={12} />
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 10, color: '#8A8A8A' }}>Framework {summary.framework_version} · run {summary.duration_sec}s</span>
        <button onClick={triggerRerun} disabled={refreshing} style={{
          fontSize: 11, padding: '5px 12px', borderRadius: 6,
          border: `1px solid ${C.bblue}`, background: refreshing ? '#eee' : C.lblue,
          color: C.blue, cursor: refreshing ? 'wait' : 'pointer', fontWeight: 500,
        }}>{refreshing ? 'Rerunning...' : 'Rerun'}</button>
      </div>

      {message && (
        <div style={{
          padding: '10px 12px', borderRadius: 8, marginBottom: 16, fontSize: 12,
          background: message.type === 'success' ? C.lgreen : C.lred,
          color: message.type === 'success' ? C.green : C.red,
          border: `1px solid ${message.type === 'success' ? C.bgreen : C.bred}`,
        }}>
          {message.text}
          <button onClick={() => setMessage(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 14 }}>×</button>
        </div>
      )}

      {/* Counter cards */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <Card label="GO"    value={summary.go_count}    color={C.green} hint={`min conviction ${summary.regime_rules?.min_conviction} · max slots ${summary.regime_rules?.max_slots}`} />
        <Card label="WATCH" value={summary.watch_count} color={C.blue}  hint="near-miss candidates" />
        <Card label="NO_GO" value={summary.no_go_count} color={C.gray}  hint="conviction/earnings filter" />
        <Card label="VETO"  value={summary.veto_count}  color={C.red}   hint="blocked entirely" />
        <Card label="Scored" value={summary.scored_count} color={C.purple} hint={`of ${summary.universe_size} universe`} />
        <Card label="Regime" value={fmtNum(summary.regime_rules?.size_multiplier, 2) + '×'} color={regimeColors.fg} hint="size multiplier" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 16 }}>
        {/* Main table */}
        <div>
          {/* Filter bar */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 4 }}>
              {['ALL','GO','WATCH','NO_GO','VETO'].map(f => (
                <button key={f} onClick={() => setDecisionFilter(f)} style={{
                  fontSize: 11, padding: '5px 12px', borderRadius: 6,
                  border: `1px solid ${decisionFilter === f ? C.blue : 'var(--border)'}`,
                  background: decisionFilter === f ? C.lblue : 'var(--bg1)',
                  color: decisionFilter === f ? C.blue : 'var(--t2)',
                  cursor: 'pointer', fontWeight: decisionFilter === f ? 600 : 400,
                }}>{f}</button>
              ))}
            </div>
            <select value={sectorFilter} onChange={e => setSectorFilter(e.target.value)}
                    style={{ fontSize: 11, padding: '5px 10px', borderRadius: 6, border: '1px solid #E5E5E0' }}>
              <option value="">All sectors</option>
              {sectors.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <span style={{ fontSize: 10, color: '#8A8A8A', marginLeft: 'auto' }}>{stocks.length} shown</span>
          </div>

          <DipSection />

          <div style={{ overflowX: "auto", background: "#FFFFFF", border: "1px solid #E5E5E0", borderRadius: 8 }}>
            <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#F1EFE8', color: '#4A4A4A' }}>
                  <th style={{ textAlign: 'left', padding: '8px 6px' }}>#</th>
                  <th style={{ textAlign: 'left', padding: '8px 6px' }}>Ticker</th>
                  <th style={{ textAlign: 'left', padding: '8px 6px' }}>Sector</th>
                  <th style={{ textAlign: 'left', padding: '8px 6px' }}>DVM</th>
                  <th style={{ textAlign: 'right', padding: '8px 6px' }}>Net</th>
                  <th style={{ textAlign: 'center', padding: '8px 6px' }} title="Alignment — 20d return >=20%, 52w position >=85, volume >=1.5x. All three together: 1.385 ATR-normalised forward return vs 1.003 baseline, 76% hit rate on 4,279 stock-days. Hover a stock's stars for its own numbers.">&#9733;</th>
                  <th style={{ textAlign: 'right', padding: '8px 6px' }} title="Price-only momentum 0-10: 20d ret, 5d ret, 52w position, distance above EMA200, volume ratio. Observational — does not affect entries.">Mom</th>
                  {/* 2026-08-15: return history + live gate status, merged into
                      the main table. NETWEB was rank 6 with nine green signals
                      but its whole 20-day trend was one +7.10% day — prev is
                      the column that makes that visible. */}
                  <th style={{ textAlign: 'right', padding: '8px 6px' }} title="20-day return">20d</th>
                  <th style={{ textAlign: 'right', padding: '8px 6px' }} title="10-day return">10d</th>
                  <th style={{ textAlign: 'right', padding: '8px 6px' }} title="5-day return">5d</th>
                  <th style={{ textAlign: 'right', padding: '8px 6px' }} title="3-day return">3d</th>
                  <th style={{ textAlign: 'right', padding: '8px 6px' }} title="Previous day. 5%+ shows red — the whole trend may be one day.">Prev</th>
                  <th style={{ textAlign: 'left', padding: '8px 6px' }} title="HELD, ENTERED, NO SLOT, or the gate currently blocking entry">Status</th>
                  <th style={{ textAlign: 'right', padding: '8px 6px' }}>★G</th>
                  <th style={{ textAlign: 'right', padding: '8px 6px' }}>★R</th>
                  <th style={{ textAlign: 'left', padding: '8px 6px' }}>Ladder</th>
                  <th style={{ textAlign: 'right', padding: '8px 6px' }}>Size</th>
                  <th style={{ textAlign: 'left', padding: '8px 6px' }}>Earnings</th>
                  <th style={{ textAlign: 'left', padding: '8px 6px' }}>Decision</th>
                </tr>
              </thead>
              <tbody>
                {stocks.map(s => (
                  <tr key={s.ticker} onClick={() => setSelectedTicker(s.ticker)} style={{ borderTop: '1px solid #E5E5E0', cursor: 'pointer' }}>
                    <td style={{ padding: '6px 6px', color: '#8A8A8A' }}>{s.rank_in_go || '-'}</td>
                    <td style={{ padding: '6px 6px', fontWeight: 600 }}>
                      {s.ticker}
                      {s.setup_type === 'DIP' && (
                        <span title="DIP setup" style={{
                          marginLeft: 6, padding: '1px 6px', fontSize: 9,
                          background: '#fff7e6', color: '#ad6800',
                          borderRadius: 3, fontWeight: 700, verticalAlign: 'middle'
                        }}>DIP</span>
                      )}
                      {s.is_traded_today && (
                        <span style={{
                          marginLeft: 6, padding: '1px 6px', fontSize: 9,
                          background: '#dcfce7', color: '#166534',
                          borderRadius: 3, fontWeight: 600, verticalAlign: 'middle'
                        }}>traded</span>
                      )}
                    </td>
                    <td style={{ padding: '6px 6px', color: '#8A8A8A', fontSize: 10 }}>{s.sector?.slice(0, 24)}</td>
                    <td style={{ padding: '6px 6px', color: '#8A8A8A', fontSize: 10 }}>{s.dvm_class?.slice(0, 22)}</td>
                    <td style={{ padding: '6px 6px', textAlign: 'right', fontWeight: 700, color: s.net_conviction >= 8 ? C.green : s.net_conviction >= 0 ? C.gray : C.red }}>{s.net_conviction}</td>
                    <td style={{ padding: '6px 6px', textAlign: 'center', fontSize: 13,
                                 cursor: 'help', letterSpacing: -1,
                                 color: s.alignment === 3 ? C.green : s.alignment === 2 ? C.amber : C.gray }}
                        title={alignWhy(s)}>
                      {s.alignment ? '\u2605'.repeat(s.alignment) : '\u00b7'}</td>
                    <td style={{ padding: '6px 6px', textAlign: 'right', fontWeight: 600,
                                 color: s.momentum_score >= 8 ? C.green : s.momentum_score >= 5 ? C.amber : C.gray }}
                        title={s.mom_ret_20d != null ? `20d ${Number(s.mom_ret_20d).toFixed(1)}% · 5d ${Number(s.mom_ret_5d ?? 0).toFixed(1)}%` : ''}>
                      {s.momentum_score ?? '—'}</td>
                    <td style={{ padding: '6px 6px', textAlign: 'right', fontSize: 10, fontWeight: 600,
                         color: s.ret_20d_live == null ? 'inherit' : (s.ret_20d_live >= 0 ? C.green : C.red) }}>
                      {s.ret_20d_live != null ? s.ret_20d_live + '%' : '—'}</td>
                    <td style={{ padding: '6px 6px', textAlign: 'right', fontSize: 10, fontWeight: 600,
                         color: s.ret_10d_live == null ? 'inherit' : (s.ret_10d_live >= 0 ? C.green : C.red) }}>
                      {s.ret_10d_live != null ? s.ret_10d_live + '%' : '—'}</td>
                    <td style={{ padding: '6px 6px', textAlign: 'right', fontSize: 10, fontWeight: 600,
                         color: s.ret_5d_live == null ? 'inherit' : (s.ret_5d_live >= 0 ? C.green : C.red) }}>
                      {s.ret_5d_live != null ? s.ret_5d_live + '%' : '—'}</td>
                    <td style={{ padding: '6px 6px', textAlign: 'right', fontSize: 10, fontWeight: 600,
                         color: s.ret_3d_live == null ? 'inherit' : (s.ret_3d_live >= 0 ? C.green : C.red) }}>
                      {s.ret_3d_live != null ? s.ret_3d_live + '%' : '—'}</td>
                    <td style={{ padding: '6px 6px', textAlign: 'right', fontSize: 10,
                         color: s.ret_prev_day == null ? 'inherit'
                                : (s.ret_prev_day >= 0 ? C.green : C.red),
                         fontWeight: (s.ret_prev_day >= 5) ? 800 : 600 }}>
                      {s.ret_prev_day != null ? s.ret_prev_day + '%' : '—'}</td>
                    <td style={{ padding: '6px 6px', fontSize: 9,
                         color: (s.gate_status === 'HELD' || s.gate_status === 'ENTERED') ? C.green : '#8A8A8A' }}>
                      {s.gate_status || '—'}</td>
                    <td style={{ padding: '6px 6px', textAlign: 'right', color: C.green }}>{s.green_score}</td>
                    <td style={{ padding: '6px 6px', textAlign: 'right', color: C.red }}>{s.red_score}</td>
                    <td style={{ padding: '6px 6px', fontSize: 10 }}>{s.ladder_tier}</td>
                    <td style={{ padding: '6px 6px', textAlign: 'right' }}>{s.decision === 'GO' ? fmtNum(s.final_size_mult, 2) + '×' : '-'}</td>
                    <td style={{ padding: '6px 6px', fontSize: 10 }}>
                      {s.days_to_earnings !== null && s.days_to_earnings <= 7
                        ? `${s.days_to_earnings}d ${s.earnings_action !== 'FULL_SIZE' ? '⚠' : ''}`
                        : ''}
                    </td>
                    <td style={{ padding: '6px 6px' }}><Pill label={s.decision} colors={DECISION_COLORS[s.decision] || DECISION_COLORS.NO_GO} size={9} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {stocks.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: '#8A8A8A', fontSize: 11 }}>No stocks match the filter</div>}
          </div>
        </div>

        {/* Veto breakdown side card */}
        <div style={{ padding: 12, alignSelf: 'flex-start', background: '#FFFFFF', border: '1px solid #E5E5E0', borderRadius: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Veto Breakdown</div>
          {vetoFilter && (
            <div style={{ marginBottom: 12, padding: '6px 10px', background: C.lblue, borderRadius: 6, fontSize: 11 }}>
              Filter: <b>{vetoFilter.replace(/^DVM_WARNING_CLASS:/, '')}</b>
              <button onClick={() => setVetoFilter('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: C.blue, fontSize: 12 }}>clear</button>
            </div>
          )}
          {vetoes.map(v => (
            <div key={v.reason} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #E5E5E0' }}>
              <div
                onClick={() => { setDecisionFilter('VETO'); setVetoFilter(v.reason); }}
                style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, cursor: 'pointer', padding: '2px 4px', borderRadius: 4, background: vetoFilter === v.reason ? C.lblue : 'transparent' }}
              >
                <span style={{ fontSize: 11, fontWeight: 600, color: C.red }}>{v.reason.replace(/^DVM_WARNING_CLASS:/, '')}</span>
                <span style={{ fontSize: 11, fontWeight: 700 }}>{v.count}</span>
              </div>
              <div style={{ fontSize: 10, color: '#8A8A8A', paddingLeft: 4 }}>
                {v.sample.map((t, i) => (
                  <span key={t}>
                    <span
                      onClick={() => setSelectedTicker(t)}
                      style={{ cursor: 'pointer', textDecoration: 'underline dotted', color: C.blue }}
                    >{t}</span>{i < v.sample.length - 1 ? ', ' : ''}
                  </span>
                ))}
                {v.count > v.sample.length ? '...' : ''}
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedTicker && <StockDetailModal ticker={selectedTicker} forDate={summary.for_date} onClose={() => setSelectedTicker(null)} />}
    </div>
  );
}
